import {ILocale} from 'data/country';
import {usageDateTypeApiUrl} from 'data/data';
import {
  IAllowStateParam,
  ICreateStatusBody,
  IPatchStatusBody,
  IPermissionState,
  IPlansData,
  ITongPlacePayment,
  ResponseSearchBackOfficeContractor,
  ResponseSearchBackOfficeTongPlace,
  ResponseSearchActiveUser,
  IUsageDateType,
  IUsageData,
  ResSearchWorker,
  IUserState,
  ICreateTongPlaceTransaction,
  IHiddenState,
  ResponseSearchUser,
  UserPermission,
  IRole,
  ITransferOwnershipCandidate,
} from 'interfaces/interface';
import {IModalState, IRegisterInfo} from 'pages/Register';
import {IUserInfo} from 'utils/Atoms';
import {AxiosSingleton} from 'utils/CustomAxios';

/**
 * 기본 API 인스턴스
 * Axios 싱글톤 인스턴스를 생성합니다.
 */
const baseAPI = new AxiosSingleton();

/**
 * 기본 Props 인터페이스
 * API 함수에서 공통으로 사용되는 Props입니다.
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsLoading - 로딩 상태 설정 함수
 * @property {React.Dispatch<React.SetStateAction<{}>>} setErrorModal - 에러 모달 설정 함수
 */
export interface defaultProps {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
}

/**
 * 샘플 함수
 * API 샘플 함수입니다.
 */
export const sample = () => {
  // API sample function
};

/**
 * API 에러 모달 설정 함수
 * API 에러 발생 시 에러 모달을 설정합니다.
 * @param {React.Dispatch<React.SetStateAction<{}>>} setErrorModal - 에러 모달 설정 함수
 * @param {any} error - 에러 객체
 * @returns {void}
 */
const setApiErrorModal = (
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>,
  error: any,
) => {
  const errorData = error?.response;
  const {
    type,
    status,
    title,
    detail,
    startTime,
    endTime,
    childStatusHistoryList,
  } = errorData || {};

  setErrorModal({
    type,
    onOff: true,
    status,
    title,
    detail,
    startTime,
    endTime,
    childStatusHistoryList,
  });
};

/**
 * API JSON 헤더 설정 함수
 * JSON 요청을 위한 헤더를 설정합니다.
 * @param {IUserInfo | null} userInfo - 사용자 정보
 * @returns {object} JSON 헤더 객체
 */
const setApiJSONHeaders = (userInfo: IUserInfo | null) => {
  return {
    'Access-Control-Allow-Origin': '*',
    Accept: 'application/json',
    authorization: userInfo?.accessToken,
  };
};

/**
 * API Multipart 헤더 설정 함수
 * Multipart 요청을 위한 헤더를 설정합니다.
 * @param {IUserInfo | null} userInfo - 사용자 정보
 * @returns {object} Multipart 헤더 객체
 */
const setApiMultipartHeaders = (userInfo: IUserInfo | null) => {
  return {
    'Content-Type': 'multipart/form-data',
    'Access-Control-Allow-Origin': '*',
    Accept: 'application/json',
    authorization: userInfo?.accessToken,
  };
};

/**
 * 인증 코드 전송 인터페이스
 * 인증 코드 전송에 필요한 파라미터를 정의합니다.
 * @property {'SIGN_UP' | 'RESET_PASSWORD'} purpose - 인증 코드 목적
 * @property {ILocale | undefined} locale - 로케일
 * @property {string} phone - 전화번호
 * @property {React.Dispatch<React.SetStateAction<IModalState>>} setModalState - 모달 상태 설정 함수
 */
export interface ISendAuthCode extends defaultProps {
  purpose: 'SIGN_UP' | 'RESET_PASSWORD';
  locale: ILocale | undefined;
  phone: string;
  setModalState: React.Dispatch<React.SetStateAction<IModalState>>;
}

/**
 * 임시 인증 코드 전송 요청
 * 회원가입 또는 비밀번호 재설정을 위한 인증 코드를 전송합니다.
 * @param {ISendAuthCode} params - 인증 코드 전송 파라미터
 * @returns {Promise<boolean | any>} 성공 시 true 또는 사용자 정보, 실패 시 false
 */
export const sendAuthCode = async ({
  purpose,
  locale,
  phone,
  setIsLoading,
  setErrorModal,
  setModalState,
}: ISendAuthCode) => {
  setIsLoading(true);
  try {
    const url = `/v1/auth-temporary-authorization-code/request-code/${purpose}`;
    const response = await baseAPI.getAxiosInstance().post(url, {
      locale: locale,
      phone: phone,
    });
    if (response?.data?.user === undefined) {
      return true;
    } else {
      return response.data.user;
    }
  } catch (error: any) {
    if (purpose === 'RESET_PASSWORD') {
      if (error?.response && error?.response?.errorCode === 404) {
        setErrorModal({
          type: error.response?.type ? error.response?.type : null,
          onOff: true,
          status: error.response?.status,
          title: 'notRegisteredContact',
          detail: 'saveErrorPhone',
          startTime: error.response.startTime,
          endTime: error.response.endTime,
          childStatusHistoryList: error.response.childStatusHistoryList,
        });
      } else if (error?.response && error?.response?.errorCode === 403) {
        setModalState(prev => ({
          ...prev,
          tempInfo: {
            state: '',
            open: true,
            info: {phone: phone},
          },
        }));
      }
      return false;
    }
    if (error?.response && setErrorModal) {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 인증 코드 확인 인터페이스
 * 인증 코드 확인에 필요한 파라미터를 정의합니다.
 * @property {'SIGN_UP' | 'RESET_PASSWORD'} purpose - 인증 코드 목적
 * @property {string} code - 인증 코드
 * @property {string} phone - 전화번호
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsLoading - 로딩 상태 설정 함수
 */
export interface ICheckAuthCode {
  purpose: 'SIGN_UP' | 'RESET_PASSWORD';
  code: string;
  phone: string;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * 임시 인증 코드 인증
 * 전송된 인증 코드를 확인합니다.
 * @param {ICheckAuthCode} params - 인증 코드 확인 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const checkAuthCode = async ({
  purpose,
  code,
  phone,
  setIsLoading,
}: ICheckAuthCode): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/auth-temporary-authorization-code/certificate/${purpose}`;
    const response = await baseAPI.getAxiosInstance().patch(url, {
      code: code,
      phone: phone,
    });

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 계정 생성 인터페이스
 * 계정 생성에 필요한 파라미터를 정의합니다.
 * @property {string | undefined} locale - 로케일
 * @property {string} phone - 전화번호
 */
export interface ICreateAccount extends defaultProps {
  locale: string | undefined;
  phone: string;
}

/**
 * 유저 계정 생성
 * 새로운 사용자 계정을 생성합니다.
 * @param {ICreateAccount} params - 계정 생성 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const createAccount = async ({
  locale,
  phone,
  setIsLoading,
  setErrorModal,
}: ICreateAccount) => {
  try {
    setIsLoading(true);
    const url = '/v1/users/create-account';
    const response = await baseAPI.getAxiosInstance().post(url, {
      locale: locale,
      phone: phone,
    });
    if (response.status === 201) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 로그인 인터페이스
 * 로그인에 필요한 파라미터를 정의합니다.
 * @property {string} phone - 전화번호
 * @property {string} password - 비밀번호
 * @property {string} [deviceToken] - 디바이스 토큰
 */
interface ISignIn extends defaultProps {
  phone: string;
  password: string;
  deviceToken?: string;
}

/**
 * 유저 로그인
 * 사용자 로그인을 처리합니다.
 * @param {ISignIn} params - 로그인 파라미터
 * @returns {Promise<any | false>} 성공 시 사용자 정보, 실패 시 false
 */
export const signIn = async ({
  phone,
  password,
  deviceToken,
  setIsLoading,
  setErrorModal,
}: ISignIn) => {
  try {
    setIsLoading(true);
    const url = '/v1/users/back-office/admins/sign-in';
    const response = await baseAPI.getAxiosInstance().post(url, {
      phone,
      password,
      deviceToken,
    });

    if (response.status === 201) {
      return response.data;
    } else {
      return response;
    }
  } catch (error: any) {
    if (
      error?.response &&
      (error?.response?.errorCode === 400 ||
        error?.response?.errorCode === 403 ||
        error?.response?.errorCode === 404)
    ) {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: 'modalErrorLoginTitle',
        detail: 'modalErrorLoginDetail',
        startTime: error.response.startTime,
        endTime: error.response.endTime,
        childStatusHistoryList: error.response.childStatusHistoryList,
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 회원가입 정보 인터페이스
 * 회원가입 정보를 포함한 인터페이스입니다.
 * @property {IRegisterInfo} registerInfo - 회원가입 정보
 */
export interface IRegisterInfoInterface extends defaultProps {
  registerInfo: IRegisterInfo;
}

/**
 * 유저 비밀번호 업데이트
 * 사용자 비밀번호를 업데이트합니다.
 * @param {IRegisterInfoInterface} params - 비밀번호 업데이트 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const updatePassword = async ({
  registerInfo,
  setIsLoading,
  setErrorModal,
}: IRegisterInfoInterface) => {
  try {
    setIsLoading(true);
    const url = '/v1/users/set-password';
    const response = await baseAPI.getAxiosInstance().patch(url, {
      password: registerInfo.password,
      phone: registerInfo.phone,
    });
    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 유저 프로필 생성
 * 사용자 프로필을 생성합니다.
 * @param {IRegisterInfoInterface} params - 프로필 생성 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const createUserProfile = async ({
  registerInfo,
  setIsLoading,
  setErrorModal,
}: IRegisterInfoInterface) => {
  try {
    setIsLoading(true);
    const url = '/v1/users/profile-initialize';
    const response = await baseAPI.getAxiosInstance().post(url, {
      phone: registerInfo.phone,
      name: registerInfo.name,
      birthDay: registerInfo.birthDay,
      gender: registerInfo.gender,
      nationality: registerInfo.nationality,
    });
    if (response.status === 201) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 유저 이용약관 동의 여부 변경
 * 사용자의 이용약관 동의 여부를 변경합니다.
 * @param {IRegisterInfoInterface} params - 약관 동의 변경 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const changeAgreementStatus = async ({
  registerInfo,
  setIsLoading,
  setErrorModal,
}: IRegisterInfoInterface) => {
  try {
    setIsLoading(true);
    const url = '/v1/users/terms-of-service-agreement';
    const response = await baseAPI.getAxiosInstance().patch(url, {
      phone: registerInfo.phone,
      service: registerInfo.service,
      privacy: registerInfo.privacy,
      location: registerInfo.location,
    });

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 서명 초기화 인터페이스
 * 서명 초기화에 필요한 파라미터를 정의합니다.
 * @property {IRegisterInfo} registerInfo - 회원가입 정보
 * @property {Blob | undefined} signature - 서명 이미지
 * @property {Blob | undefined} signature_thumbnail - 서명 썸네일 이미지
 * @property {string} authorization - 인증 토큰
 */
export interface IInitSignature extends defaultProps {
  registerInfo: IRegisterInfo;
  signature: Blob | undefined;
  signature_thumbnail: Blob | undefined;
  authorization: string;
}

/**
 * 유저 서명 초기화 함수
 * 사용자 서명을 초기화합니다.
 * @param {IInitSignature} params - 서명 초기화 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const initSignature = async ({
  registerInfo,
  signature,
  signature_thumbnail,
  setIsLoading,
  setErrorModal,
  authorization,
}: IInitSignature) => {
  try {
    setIsLoading(true);

    const sendData = new FormData();
    sendData.append('phone', registerInfo.phone);
    if (signature !== undefined && signature_thumbnail !== undefined) {
      sendData.append('signature', signature);
      sendData.append('signature_thumbnail', signature_thumbnail);
    }
    const url = '/v1/users/signature-initialize';

    const response = await baseAPI.getAxiosInstance().patch(url, sendData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        authorization: 'Bearer ' + authorization,
      },
    });
    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 임시 액세스 토큰 생성 인터페이스
 * 임시 액세스 토큰 생성에 필요한 파라미터를 정의합니다.
 * @property {string} phone - 전화번호
 */
export interface ICreateTempAccessToken extends defaultProps {
  phone: string;
}

/**
 * 유저 임시 토큰 생성
 * 사용자 임시 액세스 토큰을 생성합니다.
 * @param {ICreateTempAccessToken} params - 임시 토큰 생성 파라미터
 * @returns {Promise<string | false>} 성공 시 임시 토큰, 실패 시 false
 */
export const createTempAccessToken = async ({
  phone,
  setIsLoading,
  setErrorModal,
}: ICreateTempAccessToken) => {
  try {
    setIsLoading(true);
    const url = '/v1/users/create-temporary-access-token';
    const response = await baseAPI.getAxiosInstance().post(url, {
      phone,
    });
    if (response.status === 201) {
      return response.data.temporaryAccessToken;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 비밀번호 리셋
 * 사용자 비밀번호를 재설정합니다.
 * @param {ISignIn} params - 비밀번호 리셋 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const resetPassword = async ({
  phone,
  password,
  setIsLoading,
  setErrorModal,
}: ISignIn) => {
  try {
    setIsLoading(true);
    const url = '/v1/users/reset-password';
    const response = await baseAPI.getAxiosInstance().patch(url, {
      phone,
      password,
    });

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 백오피스 현장 검색 인터페이스
 * 백오피스 현장 검색에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {number} [count] - 페이지당 항목 수
 * @property {number} [page] - 페이지 번호
 * @property {string} [keyword] - 검색 키워드
 * @property {IAllowStateParam} [allowState] - 승인 상태
 * @property {IHiddenState} [hiddenState] - 숨김 상태
 */
interface ISearchBackOfficeTongPlace extends defaultProps {
  userInfo: IUserInfo | null;
  count?: number;
  page?: number;
  keyword?: string;
  allowState?: IAllowStateParam;
  hiddenState?: IHiddenState;
}

/**
 * 리모 백오피스 관리자용 현장 검색 API
 * 백오피스에서 승인할 현장 등을 검색하기 위한 API입니다. 키워드 파라미터에 따라 검색하며 현장의 이름, 전화번호 검색. 그리고 승인 상태에 따른 필터 적용 가능. 소유자 정보 같이 응답됨.
 * @param {ISearchBackOfficeTongPlace} params - 현장 검색 파라미터
 * @returns {Promise<ResponseSearchBackOfficeTongPlace | false>} 성공 시 현장 검색 결과, 실패 시 false
 */
export const searchBackOfficeTongPlace = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  count = 10,
  page = 1,
  keyword = '',
  allowState,
  hiddenState = 'ALL',
}: ISearchBackOfficeTongPlace): Promise<
  ResponseSearchBackOfficeTongPlace | false
> => {
  try {
    setIsLoading(true);
    const url = '/v1/tong-places/back-office/search/list';
    const response = await baseAPI.getAxiosInstance().get(url, {
      params: {
        count,
        page,
        keyword,
        allowState,
        hiddenState,
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        authorization: userInfo?.accessToken,
      },
    });
    return response.data;
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 백오피스 시공사 검색 인터페이스
 * 백오피스 시공사 검색에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {number} [count] - 페이지당 항목 수
 * @property {number} [page] - 페이지 번호
 * @property {string} [keyword] - 검색 키워드
 * @property {IAllowStateParam} [allowState] - 승인 상태
 */
interface ISearchBackOfficeContractor extends defaultProps {
  userInfo: IUserInfo | null;
  count?: number;
  page?: number;
  keyword?: string;
  allowState?: IAllowStateParam;
}

/**
 * 리모 백오피스 관리자용 시공사 검색 API
 * 백오피스에서 승인할 시공사를 검색하기 위한 API입니다. 키워드 파라미터에 따라 검색하며 시공사의 이름, 전화번호 검색. 그리고 승인 상태에 따른 필터 적용 가능. 소유자 정보 같이 응답됨.
 * @param {ISearchBackOfficeContractor} params - 시공사 검색 파라미터
 * @returns {Promise<ResponseSearchBackOfficeContractor | false>} 성공 시 시공사 검색 결과, 실패 시 false
 */
export const searchBackOfficeContractor = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  count = 10,
  page = 1,
  keyword = '',
  allowState,
}: ISearchBackOfficeContractor): Promise<
  ResponseSearchBackOfficeContractor | false
> => {
  try {
    setIsLoading(true);
    const url = '/v1/contractors/back-office/search/list';
    const response = await baseAPI.getAxiosInstance().get(url, {
      params: {
        count,
        page,
        keyword,
        allowState,
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        authorization: userInfo?.accessToken,
      },
    });
    return response.data;
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 백오피스 허용 상태 변경 인터페이스
 * 백오피스 허용 상태 변경에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {object} data - 허용 상태 변경 데이터
 * @property {string} data.contractorUUID - 시공사 UUID
 * @property {string} data.tongPlaceUUID - 현장 UUID
 * @property {IPermissionState} data.allowState - 허용 상태
 */
interface IChangeAllowStateBackOffice extends defaultProps {
  userInfo: IUserInfo | null;
  data: {
    contractorUUID: string;
    tongPlaceUUID: string;
    allowState: IPermissionState;
  };
}

/**
 * 관리자용 시공사, 현장 허용 API
 * 최초 시공사 및 단독 현장 허용 시 사용합니다. 최초 시공사면 contractorUUID 파라미터까지 세팅. 단독 현장이면 tongPlaceUUID 만 세팅.
 * @param {IChangeAllowStateBackOffice} params - 허용 상태 변경 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const changeAllowStateBackOffice = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  data,
}: IChangeAllowStateBackOffice): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/tong-places/back-office/admins/tong-places/allow-states`;
    const response = await baseAPI.getAxiosInstance().patch(url, data, {
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        Authorization: userInfo?.accessToken,
      },
    });

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    if (error.response?.errorCode === 403) {
      setErrorModal({
        onOff: true,
        title: 'updatePermissionErrorTitle',
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 백오피스 현장 생성 요청 검색 인터페이스
 * 백오피스 현장 생성 요청 검색에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {number} [count] - 페이지당 항목 수
 * @property {number} [page] - 페이지 번호
 * @property {string} [keyword] - 검색 키워드
 * @property {boolean} [isAdmin] - 관리자 여부
 */
interface ISearchBackOfficeApprovalRequestTongPlace extends defaultProps {
  userInfo: IUserInfo | null;
  count?: number;
  page?: number;
  keyword?: string;
  isAdmin?: boolean;
}

/**
 * 백오피스 현장 생성 요청 리스트 조회 API
 * 백오피스에서 현장 생성 요청 리스트를 조회합니다.
 * @param {ISearchBackOfficeApprovalRequestTongPlace} params - 현장 생성 요청 검색 파라미터
 * @returns {Promise<any | false>} 성공 시 현장 생성 요청 리스트, 실패 시 false
 */
export const searchBackOfficeApprovalRequestTongPlace = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  count = 10,
  page = 1,
  keyword = '',
  isAdmin = true,
}: ISearchBackOfficeApprovalRequestTongPlace) => {
  try {
    setIsLoading(true);
    const url = '/v1/create-tong-places-transactions/back-office/search';
    const response = await baseAPI.getAxiosInstance().get(url, {
      params: {
        count,
        page,
        keyword,
        isAdmin,
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        authorization: userInfo?.accessToken,
      },
    });
    return response.data;
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 백오피스 요청 승인 허용 상태 변경 인터페이스
 * 백오피스 요청 승인 허용 상태 변경에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {object} data - 허용 상태 변경 데이터
 * @property {string} [data.contractorUUID] - 시공사 UUID (단독 현장일 경우 없음)
 * @property {string} data.tongPlaceTransactionUUID - 현장 생성 트랜잭션 UUID
 * @property {IPermissionState} data.allowState - 허용 상태
 */
interface IChangeAllowStateBackOfficeRequestApproval extends defaultProps {
  userInfo: IUserInfo | null;
  data: {
    contractorUUID?: string; // 단독 현장일 경우 x
    tongPlaceTransactionUUID: string;
    allowState: IPermissionState;
  };
}

/**
 * 백오피스용 시공사, 현장 허용 API
 * 최초 시공사 및 최초 시공사로 만들어진 현장 허용 시 사용합니다. 최초 시공사 오너의 만들어진 권한 변경(params의 allowState에 따라), 현장 생성 및 최초 시공사 오너의 현장 오너 권한 생성 및 승인. 현장 초기화 작업 등록.
 * @param {IChangeAllowStateBackOfficeRequestApproval} params - 허용 상태 변경 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const changeAllowStateBackOfficeRequestApproval = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  data,
}: IChangeAllowStateBackOfficeRequestApproval): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/create-tong-places-transactions/back-office/tong-places/allow-states`;
    const response = await baseAPI.getAxiosInstance().patch(url, data, {
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        Authorization: userInfo?.accessToken,
      },
    });

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    if (error.response?.errorCode === 403) {
      setErrorModal({
        onOff: true,
        title: 'updatePermissionErrorTitle',
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 상태 생성 인터페이스
 * 상태 생성에 필요한 파라미터를 정의합니다.
 * @property {ICreateStatusBody} data - 상태 생성 데이터
 * @property {IUserInfo | null} userInfo - 사용자 정보
 */
export interface ICreateStatus extends defaultProps {
  data: ICreateStatusBody;
  userInfo: IUserInfo | null;
}

/**
 * 서버 상태 생성 API
 * 일반 공지를 생성 할 때에는 isNormalAnnouncement 를 true 로 세팅하고 healthStatus 는 세팅하지 않도록 합니다. 일반 공지가 아닌경우는 isNormalAnnouncement 는 세팅하지 않고 생성할 시스템 상태에 따라 healthStatus 를 세팅하도록 합니다. 주의할 점은 healthStatus 와 같이 사용하지 않도록 주의하도록 합니다.
 * @param {ICreateStatus} params - 상태 생성 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const createStatus = async ({
  data,
  setIsLoading,
  setErrorModal,
  userInfo,
}: ICreateStatus) => {
  let sendData = new FormData();

  for (const key of Object.keys(data)) {
    if (data[key]) {
      if (key === 'files') {
        const copyFileArray = data[key];
        copyFileArray &&
          copyFileArray.forEach((file: File) => {
            sendData.append(key, file);
          });
      } else {
        sendData.append(key, data[key]);
      }
    }
  }

  try {
    setIsLoading(true);
    const url = '/v1/status';
    const response = await baseAPI.getAxiosInstance().post(url, sendData, {
      headers: setApiMultipartHeaders(userInfo),
    });
    if (response.status === 201) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 상태 리스트 조회 인터페이스
 * 상태 리스트 조회에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {boolean} isNormalNotice - 일반 공지 여부
 * @property {number} [page] - 페이지 번호
 * @property {number} [count] - 페이지당 항목 수
 */
interface IGetStatusList extends defaultProps {
  userInfo: IUserInfo | null;
  isNormalNotice: boolean;
  page?: number;
  count?: number;
}

/**
 * 서버 일반 공지 & 서버 시스템 상태 리스트 조회 API
 * 서버 일반 공지 또는 서버 시스템 상태 리스트를 조회합니다.
 * @param {IGetStatusList} params - 상태 리스트 조회 파라미터
 * @returns {Promise<any | false>} 성공 시 상태 리스트, 실패 시 false
 */
export const getStatusList = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  isNormalNotice,
  page = 1,
  count = 20,
}: IGetStatusList) => {
  try {
    setIsLoading(true);
    const url = isNormalNotice
      ? `/v1/status/normal-announcements`
      : `/v1/status`;
    const response = await baseAPI.getAxiosInstance().get(url, {
      params: {count, page},
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        authorization: userInfo?.accessToken,
      },
    });
    return response.data;
  } catch (error: any) {
    setIsLoading(false);
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 상태 수정 인터페이스
 * 상태 수정에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {IPatchStatusBody} data - 상태 수정 데이터
 */
interface IPatchStatus extends defaultProps {
  userInfo: IUserInfo | null;
  data: IPatchStatusBody;
}

/**
 * 서버 상태 수정 API
 * 서버 상태를 수정합니다.
 * @param {IPatchStatus} params - 상태 수정 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const patchStatus = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  data,
}: IPatchStatus): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/status`;
    const response = await baseAPI.getAxiosInstance().patch(url, data, {
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        Authorization: userInfo?.accessToken,
      },
    });

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    if (error.response?.errorCode === 403) {
      setErrorModal({
        onOff: true,
        title: 'updatePermissionErrorTitle',
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 상태 삭제 인터페이스
 * 상태 삭제에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {string} statusUUID - 상태 UUID
 */
interface IDeleteStatus extends defaultProps {
  userInfo: IUserInfo | null;
  statusUUID: string;
}

/**
 * 서버 상태 삭제 API
 * 서버 상태를 삭제합니다.
 * @param {IDeleteStatus} params - 상태 삭제 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const deleteStatus = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  statusUUID,
}: IDeleteStatus): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/status`;
    const response = await baseAPI.getAxiosInstance().delete(url, {
      params: {statusUUID},
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        Authorization: userInfo?.accessToken,
      },
    });

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 플랜 검색 인터페이스
 * 플랜 검색에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 */
interface ISearchPlans extends defaultProps {
  userInfo: IUserInfo | null;
}

/**
 * 구독 플랜 조회
 * 사용 가능한 구독 플랜 리스트를 조회합니다.
 * @param {ISearchPlans} params - 플랜 검색 파라미터
 * @returns {Promise<IPlansData[] | false>} 성공 시 구독 플랜 리스트, 실패 시 false
 */
export const searchPlans = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
}: ISearchPlans): Promise<IPlansData[] | false> => {
  try {
    setIsLoading(true);
    const url = `/v1/subscribe-plans/available/list`;
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: setApiJSONHeaders(userInfo),
    });

    return response.data;
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 현장 검색 인터페이스
 * 현장 검색에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {string} tongPlaceUUID - 현장 UUID
 */
interface ISearchTongPlaceItem extends defaultProps {
  userInfo: IUserInfo | null;
  tongPlaceUUID: string;
}

/**
 * 현장 검색 (결제 관리 화면에서 사용)
 * 결제 관리 화면에서 사용하는 현장 검색 API입니다.
 * @param {ISearchTongPlaceItem} params - 현장 검색 파라미터
 * @returns {Promise<ITongPlacePayment | false>} 성공 시 현장 정보, 실패 시 false
 */
export const paymentTongPlace = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  tongPlaceUUID,
}: ISearchTongPlaceItem): Promise<ITongPlacePayment | false> => {
  try {
    setIsLoading(true);
    const url = `/v1/tong-places/find-one-with-payment-method/${tongPlaceUUID}`;
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: setApiJSONHeaders(userInfo),
    });
    return response.data;
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 플랜 구독 인터페이스
 * 플랜 구독에 필요한 파라미터를 정의합니다.
 * @property {string} tongPlaceUUID - 현장 UUID
 * @property {string} subscribePlanUUID - 구독 플랜 UUID
 * @property {IUserInfo | null} userInfo - 사용자 정보
 */
interface IPlanSubscribe extends defaultProps {
  tongPlaceUUID: string;
  subscribePlanUUID: string;
  userInfo: IUserInfo | null;
}

/**
 * 현장 구독 결제 시작 요청
 * 현장에 대한 구독 결제를 시작합니다.
 * @param {IPlanSubscribe} params - 플랜 구독 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const planSubscribe = async ({
  tongPlaceUUID,
  subscribePlanUUID,
  userInfo,
  setIsLoading,
  setErrorModal,
}: IPlanSubscribe) => {
  try {
    setIsLoading(true);
    const url = `/v1/tong-places/order/${tongPlaceUUID}/subscribe-plans/${subscribePlanUUID}`;

    const response = await baseAPI.getAxiosInstance().put(
      url,
      {
        tongPlaceUUID,
        subscribePlanUUID,
      },
      {
        headers: {
          ...setApiJSONHeaders(userInfo),
          'Content-Type': 'application/json;charset=UTF-8',
        },
      },
    );
    if (response.status === 200 || response.status === 201) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 활성 사용자 검색 인터페이스
 * 활성 사용자 검색에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {string} startDate - 시작 날짜
 * @property {string} endDate - 종료 날짜
 * @property {IUsageDateType} type - 날짜 타입
 */
interface ISearchActiveUser extends defaultProps {
  userInfo: IUserInfo | null;
  startDate: string;
  endDate: string;
  type: IUsageDateType;
}

/**
 * Active User 가져오는 API
 * 활성 사용자 데이터를 조회합니다.
 * @param {ISearchActiveUser} params - 활성 사용자 검색 파라미터
 * @returns {Promise<ResponseSearchActiveUser | false>} 성공 시 활성 사용자 데이터, 실패 시 false
 */
export const searchActiveUser = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  startDate,
  endDate,
  type,
}: ISearchActiveUser): Promise<ResponseSearchActiveUser | false> => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/${usageDateTypeApiUrl[type]}`;

    const response = await baseAPI.getAxiosInstance().get(url, {
      params: {startDate, endDate},
      headers: setApiJSONHeaders(userInfo),
    });

    return response.data;
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 활성 사용자 기간 검색 인터페이스
 * 활성 사용자 기간 검색에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {string} startDate - 시작 날짜
 * @property {string} endDate - 종료 날짜
 * @property {IUsageDateType} type - 날짜 타입
 */
interface ISearchActiveUserPeriod extends defaultProps {
  userInfo: IUserInfo | null;
  startDate: string;
  endDate: string;
  type: IUsageDateType;
}

/**
 * Active User 가져오는 API (기간별)
 * 관리자 계정 토큰이 필요하고, 시작일과 종료일 설정 가능합니다.
 * @param {ISearchActiveUserPeriod} params - 활성 사용자 기간 검색 파라미터
 * @returns {Promise<IUsageData | false>} 성공 시 사용량 데이터, 실패 시 false
 */
export const searchActiveUserPeriod = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  startDate,
  endDate,
  type,
}: ISearchActiveUser): Promise<IUsageData | false> => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/${usageDateTypeApiUrl[type]}/period`;

    const response = await baseAPI.getAxiosInstance().get(url, {
      params: {
        [type === 'yearly' ? 'startYear' : 'startDate']:
          type === 'yearly' ? new Date(startDate).getFullYear() : startDate,
        [type === 'yearly' ? 'endYear' : 'endDate']:
          type === 'yearly' ? new Date(endDate).getFullYear() : endDate,
      },
      headers: setApiJSONHeaders(userInfo),
      timeout: type === 'monthly' ? 60000 : 10000,
    });

    if (!response.data) {
      return false;
    }
    let result: IUsageData;
    if (type === 'daily') {
      result = {
        activeUsersList: response.data.dailyActiveUsers,
      };
    } else {
      result = {
        activeUsersList: response.data.map((item: any) => ({
          activeUsers: item[type + 'ActiveUsers'],
          date: item.startDate,
        })),
      };
    }
    return result;
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 사업자등록증 이미지 변경 인터페이스
 * 사업자등록증 이미지 변경에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {string} contractorUUID - 시공사 UUID
 * @property {File} businessRegisterNumberImg - 사업자등록증 이미지 파일
 */
interface IChangeBusinessRegisterNumberImg extends defaultProps {
  userInfo: IUserInfo | null;
  contractorUUID: string;
  businessRegisterNumberImg: File;
}

/**
 * 시공사 사업자등록증 변경 API
 * 백오피스에서만 호출 할 수 있습니다.
 * @param {IChangeBusinessRegisterNumberImg} params - 사업자등록증 이미지 변경 파라미터
 * @returns {Promise<boolean>} 성공 시 true, 실패 시 false
 */
export const changeBusinessRegisterNumberImg = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  contractorUUID,
  businessRegisterNumberImg,
}: IChangeBusinessRegisterNumberImg): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/contractors/${contractorUUID}/business-register-number-image`;
    const response = await baseAPI
      .getAxiosInstance()
      .patch(
        url,
        {businessRegisterNumberImg},
        {headers: setApiMultipartHeaders(userInfo)},
      );

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    if (error.response?.errorCode === 403) {
      setErrorModal({
        onOff: true,
        title: 'updatePermissionErrorTitle',
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 공사 유형 추가 인터페이스
 * 공사 유형 추가에 필요한 파라미터를 정의합니다.
 * @property {string} uuid - UUID
 * @property {string} type - 타입
 * @property {string} sheetName - 시트 이름
 * @property {File} excelFile - 엑셀 파일
 * @property {any} userInfo - 사용자 정보
 */
export interface IAddConstructionType extends defaultProps {
  uuid: string;
  type: string;
  sheetName: string;
  excelFile: File;
  userInfo: any;
}

/**
 * type 에 해당하는 조직에 작업공종(Excel 에서) 추가하는 API
 * Excel 파일에서 작업공종을 추가합니다.
 * @param {IAddConstructionType} params - 공사 유형 추가 파라미터
 * @returns {Promise<any | false>} 성공 시 추가 결과, 실패 시 false
 */
export const addConstructionType = async ({
  uuid,
  type,
  sheetName,
  excelFile,
  setIsLoading,
  setErrorModal,
  userInfo,
}: IAddConstructionType) => {
  try {
    const sendData = new FormData();
    sendData.append('uuid', uuid);
    sendData.append('type', type);
    sendData.append('sheetName', sheetName);
    sendData.append('excelFile', excelFile);
    setIsLoading(true);
    const url = `/v1/admins/construction-types/add/${type}/not-default/${uuid}`;
    const response = await baseAPI.getAxiosInstance().post(url, sendData, {
      headers: setApiMultipartHeaders(userInfo),
      timeout: 60000,
    });
    if (response.status === 201) {
      return response.data;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 근로자 검색 인터페이스
 * 근로자 검색에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {number} [count] - 페이지당 항목 수
 * @property {number} [page] - 페이지 번호
 * @property {string} [keyword] - 검색 키워드
 * @property {string} tongPlaceUUID - 현장 UUID
 * @property {string} [contractorUUID] - 시공사 UUID
 * @property {string} [partnerCompanyUUID] - 협력사 UUID
 * @property {string} [jobTitleUUID] - 직책 UUID
 * @property {IUserState} [userState] - 사용자 상태
 * @property {boolean} [underlyingDiseases] - 기저질환 여부
 * @property {boolean} [elderly] - 고령자 여부
 */
interface ISearchWorker extends defaultProps {
  userInfo: IUserInfo | null;
  count?: number;
  page?: number;
  keyword?: string;
  tongPlaceUUID: string;
  contractorUUID?: string;
  partnerCompanyUUID?: string;
  jobTitleUUID?: string;
  userState?: IUserState;
  underlyingDiseases?: boolean;
  elderly?: boolean;
}

/**
 * 근로자 조회 API
 * 근로자 관리-근로자 목록, 서류에서 사용하는 근로자 조회 API입니다.
 * @param {ISearchWorker} params - 근로자 검색 파라미터
 * @returns {Promise<ResSearchWorker | false>} 성공 시 근로자 리스트, 실패 시 false
 */
export const searchWorker = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  count = 20,
  page = 1,
  keyword = '',
  tongPlaceUUID,
  contractorUUID,
  partnerCompanyUUID,
  userState,
  underlyingDiseases = false,
  elderly = false,
}: ISearchWorker): Promise<ResSearchWorker | false> => {
  try {
    setIsLoading(true);

    const url = `/v1/tong-place-permission-profiles/back-office/search/workers?count=${count}&page=${page}&tongPlaceUUID=${tongPlaceUUID}&keyword=${keyword}&underlyingDiseases=${underlyingDiseases}&elderly=${elderly}${
      userState ? `&userState=${userState}` : ''
    }${partnerCompanyUUID ? `&partnerCompanyUUID=${partnerCompanyUUID}` : ''}${
      contractorUUID ? `&contractorUUID=${contractorUUID}` : ''
    }`;
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: setApiJSONHeaders(userInfo),
    });
    if (response.data) {
      return response.data;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 현장 생성 트랜잭션 사용자 검색 인터페이스
 * 현장 생성 트랜잭션 사용자 검색에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 */
interface ISearchCreateTongPlacesTransactionsUser extends defaultProps {
  userInfo: IUserInfo | null;
}

/**
 * 로그인 한 유저의 현장 생성 요청 리스트 조회 API
 * 로그인한 사용자의 현장 생성 요청 리스트를 조회합니다.
 * @param {ISearchCreateTongPlacesTransactionsUser} params - 현장 생성 트랜잭션 사용자 검색 파라미터
 * @returns {Promise<ICreateTongPlaceTransaction[] | false>} 성공 시 현장 생성 요청 리스트, 실패 시 false
 */
export const searchCreateTongPlacesTransactionsUser = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
}: ISearchCreateTongPlacesTransactionsUser): Promise<
  ICreateTongPlaceTransaction[] | false
> => {
  try {
    setIsLoading(true);
    const url = `/v1/create-tong-places-transactions/user/search`;
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: setApiJSONHeaders(userInfo),
    });
    if (response.data) return response.data;
    else return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 사용자 검색 인터페이스
 * 사용자 검색에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {number} [count] - 페이지당 항목 수
 * @property {number} [page] - 페이지 번호
 * @property {string} [keyword] - 검색 키워드
 */
interface ISearchUser extends defaultProps {
  userInfo: IUserInfo | null;
  count?: number;
  page?: number;
  keyword?: string;
}

/**
 * 백오피스용 유저 검색 API
 * 유저의 프로필에 있는 이름, 전화번호로 검색하며 페이지네이션합니다.
 * @param {ISearchUser} params - 사용자 검색 파라미터
 * @returns {Promise<ResponseSearchUser | false>} 성공 시 사용자 검색 결과, 실패 시 false
 */
export const searchUser = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  count = 20,
  page = 1,
  keyword = '',
}: ISearchUser): Promise<ResponseSearchUser | false> => {
  try {
    setIsLoading(true);
    const url = `/v1/users/back-office/admins/users/search`;
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: setApiJSONHeaders(userInfo),
      params: {count, page, keyword},
    });
    if (response.data) return response.data;
    else return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 사용자 권한 검색 인터페이스
 * 사용자 권한 검색에 필요한 파라미터를 정의합니다.
 * @property {IUserInfo | null} userInfo - 사용자 정보
 * @property {string} userUUID - 사용자 UUID
 */
interface ISearchUserPermission extends defaultProps {
  userInfo: IUserInfo | null;
  userUUID: string;
}

/**
 * 백오피스용 특정유저의 모든 권한, 권한 프로필 조회 API
 * 특정 사용자의 모든 권한과 권한 프로필을 조회합니다.
 * @param {ISearchUserPermission} params - 사용자 권한 검색 파라미터
 * @returns {Promise<UserPermission[] | false>} 성공 시 사용자 권한 리스트, 실패 시 false
 */
export const searchUserPermission = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  userUUID,
}: ISearchUserPermission): Promise<UserPermission[] | false> => {
  try {
    setIsLoading(true);
    const url = `/v1/users/back-office/admins/users/search/permissions`;
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: setApiJSONHeaders(userInfo),
      params: {userUUID},
    });
    if (response.data) return response.data.list;
    else return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface IRegisterSalesManagerInfo {
  phone: string;
  password: string;
  email: string;
  address: string;
  addressDetail: string;
  nationality: string;
  representativeIndustry: string;
}
export interface IRegisterSalesManager extends defaultProps {
  info: IRegisterSalesManagerInfo;
  locale: ILocale;
  userInfo: IUserInfo;
}

/**
 백오피스용 세일즈 매니저 계정 생성 API. 리모 백오피스 관리자만 호출 가능하다.
 @param parameter
 @returns
 @example
 ```typescript
const result = await registerSalesManager({
            info: IRegisterSalesManagerInfo;
            locale: ILocale;
            userInfo: IUserInfo;
        });
console.log(result); // true or false
```
 */

export const registerSalesManager = async ({
  info,
  locale,
  userInfo,
  setIsLoading,
  setErrorModal,
}: IRegisterSalesManager) => {
  try {
    setIsLoading(true);
    const data = {
      ...info,
      locale,
    };
    const url = `/v1/users/back-office/admins/sales-managers/create-account`;
    const response = await baseAPI.getAxiosInstance().post(
      url,
      {...data},
      {
        headers: setApiJSONHeaders(userInfo),
      },
    );
    if (response.status === 201 || response.status === 200) {
      return response.data;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface IGetReferralCodeList extends defaultProps {
  count?: number;
  page?: number;
  isUse?: boolean;
  keyword?: string;
  userInfo: IUserInfo;
}

/**
백오피스용 특정유저의 모든 권한, 권한 프로필 조회 API
 @param parameter
 @returns {}
 @example
 ```typescript

const result = await getReferralCodeList({
                userInfo,
                setIsLoading,
                setErrorModal,
                userUUID: 'userUUID',
        });
console.log(result); 
```
 */

export const getReferralCodeList = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  count,
  page,
  isUse,
  keyword,
}: IGetReferralCodeList) => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/referral-codes/list`;
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: setApiJSONHeaders(userInfo),
      params: {
        count,
        page,
        isUse: isUse,
        keyword,
      },
    });
    if (response.data) return response.data;
    else return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface IUpdateRerralCode extends defaultProps {
  userInfo: IUserInfo | null;
  referralCodeUUID: string;
  code: string;
  expiredValue: number;
  offset: 'day' | 'week' | 'month' | 'year';
}

/**
추천 코드 수정하는 API. 리모 관리자가 Sales Manager 에게 발행된 추천코드를 수정할 때 사용하는 API.
 @param parameter
 @returns {boolean}
 @example
 ```typescript
const result = await updateRerralCode({
          userInfo,
          setIsLoading,
          setErrorModal,
          contractorUUID: 'contractorUUID',
        });
console.log(result); // true || false
```
 */

export const updateRerralCode = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  referralCodeUUID,
  code,
  expiredValue,
  offset,
}: IUpdateRerralCode): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/referral-codes`;
    const response = await baseAPI.getAxiosInstance().patch(
      url,
      {
        referralCodeUUID,
        code,
        expiredParams: {
          expiredValue: Number(expiredValue),
          offset,
        },
      },
      {headers: setApiJSONHeaders(userInfo)},
    );

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    console.log('error', error);
    if (error.response?.errorCode === 403) {
      setErrorModal({
        onOff: true,
        title: 'updatePermissionErrorTitle',
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface ISearchBackOfficeSalesManagerList extends defaultProps {
  userInfo: IUserInfo | null;
  count?: number;
  page?: number;
  keyword?: string;
}

/**
 백오피스용 Sales Manager 검색 API. Sales Manager 프로필에 있는 이름, 전화번호로 검색하며 페이지네이션한다.
 @param parameter
 @returns
 @example
 ```typescript
const result = await searchBackOfficeSalesManagerList({
          userInfo,
          setIsLoading,
          setErrorModal,
          count,
          page,
          keyword,
        });
console.log(result); 
```
 */

export const searchBackOfficeSalesManagerList = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  count = 10,
  page = 1,
  keyword = '',
}: ISearchBackOfficeSalesManagerList) => {
  try {
    setIsLoading(true);
    const url = '/v1/users/back-office/admins/sales-managers/search';
    const response = await baseAPI.getAxiosInstance().get(url, {
      params: {
        count,
        page,
        keyword,
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        authorization: userInfo?.accessToken,
      },
    });
    return response.data;
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

export interface IRegisterReferralCode extends defaultProps {
  userInfo: IUserInfo | null;
  code: string;
  ownerUUID: string;
  expiredValue: number;
  offset: 'day' | 'week' | 'month' | 'year';
}

/**
 추천 코드 발급하는 API. 백오피스 관리자 페이지에서 세일즈매니저에 추천코드 발행시 사용한다.
기획이 나중에 바뀔 경우를 생각해서 일단 ownerUUID(발행한 추천코드를 소유할 소유자 여기서는 세일즈 매니저) 는 optional 처리 되어있으며
현재 기획에서는 무조건 해당 추천코드 소유할 세일즈 매니저 uuid 를 ownerUUID 에 세팅해서 보내도록한다.
 @param parameter
 @returns
 @example
 ```typescript
const result = await registerReferralCode({
            userInfo,
            setIsLoading,
            setErrorModal,
            code,
            ownerUUID,
            expiredParams,
        });
console.log(result); // true or false
```
 */

export const registerReferralCode = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  code,
  ownerUUID,
  expiredValue,
  offset,
}: IRegisterReferralCode) => {
  try {
    setIsLoading(true);
    const data = {
      code,
      ownerUUID,
      expiredParams: {
        expiredValue,
        offset,
      },
    };
    const url = `/v1/admins/referral-codes`;
    const response = await baseAPI.getAxiosInstance().post(
      url,
      {
        ...data,
        expiredParams: {
          expiredValue: Number(expiredValue),
          offset,
        },
      },
      {
        headers: setApiJSONHeaders(userInfo),
      },
    );
    if (response.status === 201 || response.status === 200) {
      return response.data;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface IDiscardRerralCode extends defaultProps {
  userInfo: IUserInfo | null;
  referralCodeUUID: string;
}

/**
추천 코드 폐기(삭제랑 다름) API. 리모 관리자가 Sales Manager 에게 발행된 추천코드를 폐기(사용불가처리) 때 사용하는 API.
 @param parameter
 @returns {boolean}
 @example
 ```typescript
const result = await discardRerralCode({
            userInfo,
            setIsLoading,
            setErrorModal,
            referralCodeUUID,
        });
console.log(result); // true || false
```
 */

export const discardRerralCode = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  referralCodeUUID,
}: IDiscardRerralCode): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/referral-codes/discard/${referralCodeUUID}`;
    const response = await baseAPI
      .getAxiosInstance()
      .patch(url, {}, {headers: setApiJSONHeaders(userInfo)});

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    console.log('error', error);
    if (error.response?.errorCode === 403) {
      setErrorModal({
        onOff: true,
        title: 'updatePermissionErrorTitle',
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface IActivateRerralCode extends defaultProps {
  userInfo: IUserInfo | null;
  referralCodeUUID: string;
}

/**
추천 코드 활성화
 @param parameter
 @returns {boolean}
 @example
 ```typescript
const result = await activateRerralCode({
            userInfo,
            setIsLoading,
            setErrorModal,
            referralCodeUUID,
        });
console.log(result); // true || false
```
 */

export const activateRerralCode = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  referralCodeUUID,
}: IActivateRerralCode): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/referral-codes/activate/${referralCodeUUID}`;
    const response = await baseAPI
      .getAxiosInstance()
      .patch(url, {}, {headers: setApiJSONHeaders(userInfo)});

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    console.log('error', error);
    if (error.response?.errorCode === 403) {
      setErrorModal({
        onOff: true,
        title: 'updatePermissionErrorTitle',
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface IDeleteRerralCode extends defaultProps {
  userInfo: IUserInfo | null;
  referralCodeUUID: string;
}

/**
추천 코드 폐기(삭제랑 다름) API. 리모 관리자가 Sales Manager 에게 발행된 추천코드를 폐기(사용불가처리) 때 사용하는 API.
 @param parameter
 @returns {boolean}
 @example
 ```typescript
const result = await deleteRerralCode({
            userInfo,
            setIsLoading,
            setErrorModal,
            referralCodeUUID,
        });
console.log(result); // true || false
```
 */

export const deleteRerralCode = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  referralCodeUUID,
}: IDeleteRerralCode): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/referral-codes/discard/${referralCodeUUID}`;
    const response = await baseAPI
      .getAxiosInstance()
      .delete(url, {headers: setApiJSONHeaders(userInfo)});

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    console.log('error', error);
    if (error.response?.errorCode === 403) {
      setErrorModal({
        onOff: true,
        title: 'updatePermissionErrorTitle',
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface ISearchSalesProfileDetail extends defaultProps {
  userInfo: IUserInfo | null;
  salesManagerUUID: string;
}

/**
 세일즈 매니저 페이지에서 세일즈 매니저 프로필 상세 조회 API.
 @param parameter
 @returns
 @example
 ```typescript
const result = await searchSalesProfileDetail({
          userInfo,
          setIsLoading,
          setErrorModal,
        });
console.log(result);
```
 */

export const searchSalesProfileDetail = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  salesManagerUUID,
}: ISearchSalesProfileDetail) => {
  try {
    setIsLoading(true);
    const url = `/v1/users/back-office/admins/sales-managers/${salesManagerUUID}/profiles/detail`;
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        authorization: userInfo?.accessToken,
      },
    });
    return response.data;
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface IUpdateSalesManagerProfile extends defaultProps {
  userInfo: IUserInfo | null;
  name: string;
  email: string;
  userUUID: string;
  salesManagerUUID: string;
}

/**
  세일즈 매니저 페이지에서 세일즈 매니저 프로필 수정(비밀번호제외) API.
   @param parameter
   @returns {boolean}
   @example
   ```typescript
  const result = await updateSalesManagerProfile({
              userInfo,
              setIsLoading,
              setErrorModal,
              referralCodeUUID,
          });
  console.log(result); // true || false
  ```
   */

export const updateSalesManagerProfile = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  name,
  email,
  userUUID,
  salesManagerUUID,
}: IUpdateSalesManagerProfile): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/users/back-office/admins/${salesManagerUUID}/sales-managers/profiles`;
    const response = await baseAPI.getAxiosInstance().patch(
      url,
      {name, email, userUUID, salesManagerUUID},
      {
        headers: {
          'Access-Control-Allow-Origin': '*',
          Accept: 'application/json',
          authorization: userInfo?.accessToken,
        },
      },
    );

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    console.log('error', error);
    if (error.response?.errorCode === 403) {
      setErrorModal({
        onOff: true,
        title: 'updatePermissionErrorTitle',
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface IGetUserPermissions extends defaultProps {
  userInfo: IUserInfo | null;
  userUUID: string;
}

/**
 백오피스용 특정유저의 모든 권한, 권한 프로필 조회 API
 @param parameter
 @returns
 @example
 ```typescript
const result = await getUserPermissions({
          userInfo,
          setIsLoading,
          setErrorModal,
        });
console.log(result);
```
 */

export const getUserPermissions = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  userUUID,
}: IGetUserPermissions) => {
  try {
    setIsLoading(true);
    const url = `/v1/users/back-office/admins/users/search/permissions`;
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        authorization: userInfo?.accessToken,
      },
      params: {
        userUUID,
      },
    });
    return response.data;
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface IChangeUsersPermissions extends defaultProps {
  userInfo: IUserInfo | null;
  data: {
    type:
      | 'ContractorPermission'
      | 'PartnerCompanyPermission'
      | 'TongPlacePermission';
    permissionUUID: string;
    userUUID: string;
    role?: IRole;
    state: IPermissionState;
  };
}

/**
백오피스용 특정유저의 특정 권한과 승인상태 수정 API API. 백오피스 페이지 관리자가 수동으로 권한 수정시에만 사용.
 @param parameter
 @returns {boolean}
 @example
 ```typescript
const result = await changeUsersPermissions({
          userInfo,
          setIsLoading,
          setErrorModal,
          data: {
                  type,
                  permissionUUID,
                  userUUID,
                  role,
                  state,
          }
        });
console.log(result); // true || false
```
 */

export const changeUsersPermissions = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  data,
}: IChangeUsersPermissions): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/users/back-office/admins/users/permissions`;
    const response = await baseAPI.getAxiosInstance().patch(url, data, {
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        Authorization: userInfo?.accessToken,
      },
    });

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    console.log('error', error);
    if (error.response?.errorCode === 403) {
      setErrorModal({
        onOff: true,
        title: 'updatePermissionErrorTitle',
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

export interface IRestoreTongPlacePermission extends defaultProps {
  userInfo: IUserInfo | null;
  userUUID: string;
  tongPlaceUUID: string;
}

/**
 * 백오피스용 현장 권한 복원 API.
 * 탈퇴(soft-delete)된 현장 권한과 권한 프로필을 복원합니다.
 * @param parameter
 * @returns {boolean}
 */
export const restoreTongPlacePermission = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  userUUID,
  tongPlaceUUID,
}: IRestoreTongPlacePermission): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/tong-place-permissions/back-office/restore`;
    const response = await baseAPI.getAxiosInstance().patch(
      url,
      {userUUID, tongPlaceUUID},
      {
        headers: {
          'Access-Control-Allow-Origin': '*',
          Accept: 'application/json',
          Authorization: userInfo?.accessToken,
        },
      },
    );
    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    console.log('error', error);
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

export interface IChangePaymentState extends defaultProps {
  userInfo: IUserInfo | null;
  isApproved: boolean;
  tongPlaceUUID: string;
  memo?: string;
}

/**
 백오피스 관리자가 현장 결재 데이터 생성 API.
 @param parameter
 @returns
 @example
 ```typescript
const result = await changePaymentState({
            userInfo,
            isApproved,
            tongPlaceUUID,
            memo
        });
console.log(result); // true or false
```
 */

export const changePaymentState = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  isApproved,
  tongPlaceUUID,
  memo,
}: IChangePaymentState) => {
  try {
    setIsLoading(true);
    const data = {
      isApproved,
      tongPlaceUUID,
      memo,
    };
    const url = `/v1/approve/back-office/tong-places`;
    const response = await baseAPI.getAxiosInstance().post(
      url,
      {
        ...data,
      },
      {
        headers: setApiJSONHeaders(userInfo),
      },
    );
    if (response.status === 201 || response.status === 200) {
      return response.data;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface IChangeTongPlaceOperationStates extends defaultProps {
  userInfo: IUserInfo | null;
  tongPlaceUUID: string;
  constructionStartDate: string;
  constructionEndDate: string;
}

/**
관리자용 현장 운영상태 및 공사기간 수정 API.
공사기간이 지나고 나서 공사기간 수정을 요청하는 경우 운영상태는 이미 완료로 고정임. 사용자로부터 변경 요청이 오면 백오피스에서 처리해주도록 한다.
 @param parameter
 @returns {boolean}
 @example
 ```typescript
const result = await changeTongPlaceOperationStates({
          userInfo,
          tongPlaceUUID,
          constructionStartDate,
          constructionEndDate
        });
console.log(result); // true || false
```
 */

export const changeTongPlaceOperationStates = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  tongPlaceUUID,
  constructionStartDate,
  constructionEndDate,
}: IChangeTongPlaceOperationStates): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/tong-places/back-office/admins/tong-places/operation-states`;
    const response = await baseAPI.getAxiosInstance().patch(
      url,
      {
        constructionStartDate,
        constructionEndDate,
        tongPlaceUUID,
      },
      {
        headers: {
          'Access-Control-Allow-Origin': '*',
          Accept: 'application/json',
          Authorization: userInfo?.accessToken,
        },
      },
    );

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    console.log('error', error);
    if (error.response?.errorCode === 403) {
      setErrorModal({
        onOff: true,
        title: 'updatePermissionErrorTitle',
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface ISearchBackOfficePartnerCompany extends defaultProps {
  userInfo: IUserInfo | null;
  count?: number;
  page?: number;
  keyword?: string;
}

/**
 백오피스 협력사 리스트 조회 API
 @param parameter
 @returns
 @example
 ```typescript
const result = await searchBackOfficePartnerCompany({
          userInfo,
          setIsLoading,
          setErrorModal,
          count,
          page,
          keyword,
          isAdmin
        });
console.log(result); 
```
 */

export const searchBackOfficePartnerCompany = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  count = 10,
  page = 1,
  keyword = '',
}: ISearchBackOfficePartnerCompany) => {
  try {
    setIsLoading(true);
    const url = '/v1/admins/partner-companies/list';
    const response = await baseAPI.getAxiosInstance().get(url, {
      params: {
        count,
        page,
        keyword,
      },
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: 'application/json',
        authorization: userInfo?.accessToken,
      },
    });
    return response.data;
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 백오피스 현장별 출근 인원(distinct) 집계 인터페이스
 */
interface ISearchBackOfficeSiteAttendeeCounts extends defaultProps {
  userInfo: IUserInfo | null;
  tongPlaceUUIDList: string[];
  startDate: string;
  endDate: string;
}

/**
 * 백오피스 현장별 출근 인원(distinct) 집계 API
 * 기간 내 각 현장에 출근한 인원 수를 한 번에 조회합니다. (userUUID 기준 중복 제거)
 * @returns 성공 시 {현장UUID: 인원수} 맵, 실패 시 false
 */
export const searchBackOfficeSiteAttendeeCounts = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  tongPlaceUUIDList,
  startDate,
  endDate,
}: ISearchBackOfficeSiteAttendeeCounts): Promise<
  {[tongPlaceUUID: string]: number} | false
> => {
  try {
    setIsLoading(true);
    const url = '/v1/tong-place-attendances/back-office/attendance-counts';
    const response = await baseAPI
      .getAxiosInstance()
      .post(
        url,
        {tongPlaceUUIDList, startDate, endDate},
        {headers: setApiJSONHeaders(userInfo)},
      );
    return response.data?.counts ?? {};
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

export interface IChangePartnerCompanyName extends defaultProps {
  userInfo: IUserInfo | null;
  partnerCompanyUUID: string;
  name: string;
}

/**
 협력사 단건 상세 조회 API

 @param parameter
 @returns
 @example
 ```typescript
const result = await changePartnerCompanyName({
            userInfo,
            partnerCompanyUUID,
            name,
        });
console.log(result); // true or false
```
 */

export const changePartnerCompanyName = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  partnerCompanyUUID,
  name,
}: IChangePartnerCompanyName) => {
  try {
    setIsLoading(true);
    const data = {
      name,
    };
    const url = `/v1/admins/partner-companies/${partnerCompanyUUID}/properties/name`;
    const response = await baseAPI.getAxiosInstance().patch(
      url,
      {
        ...data,
      },
      {
        headers: setApiJSONHeaders(userInfo),
      },
    );
    if (response.status === 201 || response.status === 200) {
      return response.data;
    } else {
      return false;
    }
  } catch (error: any) {
    setErrorModal({
      type: error.response?.type ? error.response?.type : null,
      onOff: true,
      status: error.response?.status,
      title: error.response?.title,
      detail: error.response?.detail,
      startTime: error.response?.startTime,
      endTime: error.response?.endTime,
      childStatusHistoryList: error.response?.childStatusHistoryList,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

// =====================================================
// 퇴사 처리 API
// =====================================================

/**
 * 퇴사 가능 여부 확인 API 파라미터
 */
export interface ICheckResignation extends defaultProps {
  userInfo: IUserInfo | null;
  userUUID: string;
  contractorUUID: string;
}

/**
 * 퇴사 가능 여부 확인 응답
 */
export interface IResignationCheckResponse {
  canResign: boolean;
  ownerInfo: {
    isContractorOwner: boolean;
    partnerCompaniesAsOwner: Array<{uuid: string; name: string}>;
    teamsAsOwner: Array<{uuid: string; name: string}>;
    tongPlacesAsOwner: Array<{uuid: string; name: string}>;
  };
  requiresTransfer: boolean;
}

/**
 * 퇴사 가능 여부 확인 API
 * 사용자의 OWNER 현황을 확인합니다.
 */
export const checkResignation = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  userUUID,
  contractorUUID,
}: ICheckResignation): Promise<IResignationCheckResponse | false> => {
  try {
    setIsLoading(true);
    const url = '/v1/contractor-permissions/resignation-check';
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: setApiJSONHeaders(userInfo),
      params: {userUUID, contractorUUID},
    });
    if (response.data) return response.data;
    else return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 이양 대상 목록 조회 API 파라미터
 */
export interface IGetTransferCandidates extends defaultProps {
  userInfo: IUserInfo | null;
  userUUID: string;
  contractorUUID: string;
  isContractorOwner: boolean;
  source: 'backoffice' | 'pc-admin';
}

/**
 * 이양 후보자 정보
 */
export interface ITransferCandidate {
  userUUID: string;
  name: string;
  phone: string;
  role: string;
  isOtherContractorOwner: boolean;
}

/**
 * 이양 대상 목록 응답
 */
export interface ITransferCandidatesResponse {
  candidates: ITransferCandidate[];
  fallbackLevel: 'CO_OWNER' | 'MANAGER_PLUS' | 'ALL_WORKERS' | 'NONE';
  message?: string;
}

/**
 * 이양 대상 목록 조회 API
 * OWNER 권한을 이양받을 수 있는 대상자 목록을 조회합니다.
 */
export const getTransferCandidates = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  userUUID,
  contractorUUID,
  isContractorOwner,
  source,
}: IGetTransferCandidates): Promise<ITransferCandidatesResponse | false> => {
  try {
    setIsLoading(true);
    const url = '/v1/contractor-permissions/transfer-candidates';
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: setApiJSONHeaders(userInfo),
      params: {userUUID, contractorUUID, isContractorOwner, source},
    });
    if (response.data) return response.data;
    else return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 퇴사 처리 API 파라미터
 */
export interface IResign extends defaultProps {
  userInfo: IUserInfo | null;
  userUUID: string;
  contractorUUID: string;
  newOwnerUserUUID?: string;
  isContractorOwner: boolean;
  source: 'backoffice' | 'pc-admin';
}

/**
 * 퇴사 처리 응답
 */
export interface IResignResponse {
  success: boolean;
  message: string;
  deletedPermissions: {
    contractor: number;
    partnerCompany: number;
    team: number;
    tongPlace: number;
  };
  transferredOwnership: {
    contractor: boolean;
    partnerCompanies: string[];
    teams: string[];
    tongPlaces: string[];
  };
  forcedClockOut: {
    contractor: number;
    partnerCompany: number;
    tongPlace: number;
  };
  autoApprovedDocuments: number;
  warnings?: string[];
}

/**
 * 퇴사 처리 API (BackOffice용)
 * 사용자의 퇴사를 처리합니다.
 */
export const resign = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  userUUID,
  contractorUUID,
  newOwnerUserUUID,
  isContractorOwner,
  source,
}: IResign): Promise<IResignResponse | false> => {
  try {
    setIsLoading(true);
    const url = '/v1/contractor-permissions/resign';
    const data = {
      userUUID,
      contractorUUID,
      newOwnerUserUUID,
      isContractorOwner,
      source,
    };
    const response = await baseAPI.getAxiosInstance().post(url, data, {
      headers: setApiJSONHeaders(userInfo),
    });
    if (response.data) return response.data;
    else return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

// =====================================================
// OWNER 이양 API
// =====================================================

/**
 * OWNER 이양 후보 조회 API 파라미터
 */
export interface IGetTransferOwnershipCandidates extends defaultProps {
  userInfo: IUserInfo | null;
  userUUID: string;
  targetType: string;
  targetUUID: string;
}

/**
 * OWNER 이양 후보 조회 API
 * 특정 엔티티의 OWNER 이양 후보를 조회합니다.
 */
export const getTransferOwnershipCandidates = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  userUUID,
  targetType,
  targetUUID,
}: IGetTransferOwnershipCandidates): Promise<
  ITransferOwnershipCandidate[] | false
> => {
  try {
    setIsLoading(true);
    const url = '/v1/contractor-permissions/transfer-ownership-candidates';
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: setApiJSONHeaders(userInfo),
      params: {userUUID, targetType, targetUUID},
    });
    if (response.data) return response.data;
    else return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 시공사 OWNER 이양 API 파라미터
 */
export interface ITransferContractorOwnership extends defaultProps {
  userInfo: IUserInfo | null;
  userUUID: string;
  contractorUUID: string;
  newOwnerUserUUID: string;
}

/**
 * OWNER 이양 응답
 */
export interface ITransferOwnershipResponse {
  success: boolean;
  message: string;
  transferred: {
    targetType: string;
    targetUUID: string;
    targetName: string;
    previousOwnerUUID: string;
    newOwnerUUID: string;
    newOwnerName: string;
  };
}

/**
 * 시공사 OWNER 이양 API
 */
export const transferContractorOwnership = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  userUUID,
  contractorUUID,
  newOwnerUserUUID,
}: ITransferContractorOwnership): Promise<
  ITransferOwnershipResponse | false
> => {
  try {
    setIsLoading(true);
    const url = '/v1/contractor-permissions/transfer-contractor-ownership';
    const response = await baseAPI.getAxiosInstance().post(
      url,
      {
        userUUID,
        contractorUUID,
        newOwnerUserUUID,
      },
      {
        headers: setApiJSONHeaders(userInfo),
      },
    );
    if (response.data) return response.data;
    else return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * 하위 엔티티 OWNER 이양 API 파라미터
 */
export interface ITransferEntityOwnership extends defaultProps {
  userInfo: IUserInfo | null;
  userUUID: string;
  newOwnerUserUUID: string;
  targetType: string;
  targetUUID: string;
}

/**
 * 하위 엔티티(협력사/팀/현장) OWNER 이양 API
 */
export const transferEntityOwnership = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  userUUID,
  newOwnerUserUUID,
  targetType,
  targetUUID,
}: ITransferEntityOwnership): Promise<ITransferOwnershipResponse | false> => {
  try {
    setIsLoading(true);
    const url = '/v1/contractor-permissions/transfer-entity-ownership';
    const response = await baseAPI.getAxiosInstance().post(
      url,
      {
        userUUID,
        newOwnerUserUUID,
        targetType,
        targetUUID,
      },
      {
        headers: setApiJSONHeaders(userInfo),
      },
    );
    if (response.data) return response.data;
    else return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

// =============================================
// 감사 로그 & 데이터 관리 API
// =============================================

export interface IGetAuditLogs extends defaultProps {
  userInfo: IUserInfo | null;
  entityType?: string;
  entityUUID?: string;
  tongPlaceUUID?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export const getAuditLogs = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  entityType,
  entityUUID,
  tongPlaceUUID,
  from,
  to,
  page = 1,
  limit = 20,
}: IGetAuditLogs) => {
  try {
    setIsLoading(true);
    const params = new URLSearchParams();
    if (entityType) params.append('entityType', entityType);
    if (entityUUID) params.append('entityUUID', entityUUID);
    if (tongPlaceUUID) params.append('tongPlaceUUID', tongPlaceUUID);
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    params.append('page', String(page));
    params.append('limit', String(limit));

    const response = await baseAPI
      .getAxiosInstance()
      .get(`/v1/admins/audit-logs?${params.toString()}`, {
        headers: setApiJSONHeaders(userInfo),
      });
    if (response.status === 200) return response.data;
    return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

export interface IRetryAuditLogSync extends defaultProps {
  userInfo: IUserInfo | null;
  logId: string;
}

/**
 * audit log 의 Snapshot Sync 재시도 (97 A1).
 * 응답: { skipped: boolean, syncResult: { status, affectedDocuments, error? } }
 * - skipped=true 면 이미 success 상태 (재시도 불필요)
 * - skipped=false + status='success' 면 정상 재시도 성공
 * - status='failed'/'partial' 이면 응답의 error 메시지 확인
 */
export const retryAuditLogSync = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  logId,
}: IRetryAuditLogSync) => {
  try {
    setIsLoading(true);
    const response = await baseAPI
      .getAxiosInstance()
      .post(
        `/v1/admins/audit-logs/${logId}/retry-sync`,
        {},
        {headers: setApiJSONHeaders(userInfo)},
      );
    if (response.status === 200 || response.status === 201)
      return response.data;
    return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

export interface IChangeUserName extends defaultProps {
  userInfo: IUserInfo | null;
  userUUID: string;
  name: string;
}

export const changeUserName = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  userUUID,
  name,
}: IChangeUserName) => {
  try {
    setIsLoading(true);
    const response = await baseAPI
      .getAxiosInstance()
      .patch(
        `/v1/admins/users/${userUUID}/profile/name`,
        {name},
        {headers: setApiJSONHeaders(userInfo)},
      );
    if (response.status === 200 || response.status === 201)
      return response.data;
    return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

export interface IChangeTongPlaceName extends defaultProps {
  userInfo: IUserInfo | null;
  tongPlaceUUID: string;
  name: string;
}

export const changeTongPlaceName = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  tongPlaceUUID,
  name,
}: IChangeTongPlaceName) => {
  try {
    setIsLoading(true);
    const response = await baseAPI
      .getAxiosInstance()
      .patch(
        `/v1/admins/tong-places/${tongPlaceUUID}/name`,
        {name},
        {headers: setApiJSONHeaders(userInfo)},
      );
    if (response.status === 200 || response.status === 201)
      return response.data;
    return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

export interface IChangeBusinessRegisterNumber extends defaultProps {
  userInfo: IUserInfo | null;
  entityType: 'contractors' | 'partner-companies';
  entityUUID: string;
  businessRegisterNumber: string;
}

export const changeBusinessRegisterNumber = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  entityType,
  entityUUID,
  businessRegisterNumber,
}: IChangeBusinessRegisterNumber) => {
  try {
    setIsLoading(true);
    const response = await baseAPI
      .getAxiosInstance()
      .patch(
        `/v1/admins/${entityType}/${entityUUID}/business-register-number`,
        {businessRegisterNumber},
        {headers: setApiJSONHeaders(userInfo)},
      );
    if (response.status === 200 || response.status === 201)
      return response.data;
    return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

export interface IChangeContractorName extends defaultProps {
  userInfo: IUserInfo | null;
  contractorUUID: string;
  name: string;
}

export const changeContractorName = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  contractorUUID,
  name,
}: IChangeContractorName) => {
  try {
    setIsLoading(true);
    const response = await baseAPI
      .getAxiosInstance()
      .patch(
        `/v1/admins/contractors/${contractorUUID}/name`,
        {name},
        {headers: setApiJSONHeaders(userInfo)},
      );
    if (response.status === 200 || response.status === 201)
      return response.data;
    return false;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

// ────────────────────────────────────────────
// Phase 2.10 — HQ 접근 권한 관리 (BackOffice)
// ────────────────────────────────────────────

interface IListHqAccessGrants extends defaultProps {
  userInfo: IUserInfo | null;
  contractorUUID: string;
}

/**
 * 시공사별 활성 HQ access grant 목록 조회.
 * Phase 2.10 — 본사관리자 접근 권한 관리.
 */
export const listHqAccessGrants = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  contractorUUID,
}: IListHqAccessGrants): Promise<{items: any[]} | false> => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/headquarters-access-grants`;
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: setApiJSONHeaders(userInfo),
      params: {contractorUUID},
    });
    return response.data;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface ICreateHqGrant extends defaultProps {
  userInfo: IUserInfo | null;
  contractorUUID: string;
  targetUserUUID: string;
  reason?: string;
}

/**
 * SEED grant 부여. 운영팀 재량으로 시공사당 N명 가능 (Phase 2.10 v3).
 */
export const createHqSeedGrant = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  contractorUUID,
  targetUserUUID,
  reason,
}: ICreateHqGrant): Promise<{grant: any} | false> => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/headquarters-access-grants/seed`;
    const response = await baseAPI
      .getAxiosInstance()
      .post(
        url,
        {contractorUUID, targetUserUUID, reason},
        {headers: setApiJSONHeaders(userInfo)},
      );
    return response.data;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

/**
 * INVITED grant 부여 (BackOffice 직접). Phase 2.10 v3 — SEED 외 운영팀이 직접 추가.
 */
export const createHqInvitedGrant = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  contractorUUID,
  targetUserUUID,
  reason,
}: ICreateHqGrant): Promise<{grant: any} | false> => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/headquarters-access-grants/invited`;
    const response = await baseAPI
      .getAxiosInstance()
      .post(
        url,
        {contractorUUID, targetUserUUID, reason},
        {headers: setApiJSONHeaders(userInfo)},
      );
    return response.data;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface IRevokeHqGrant extends defaultProps {
  userInfo: IUserInfo | null;
  grantUUID: string;
  reason: string;
}

/**
 * grant 회수. revokedReason 필수.
 * SEED 회수 시 같은 시공사 INVITED 자동 회수.
 */
export const revokeHqGrant = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  grantUUID,
  reason,
}: IRevokeHqGrant): Promise<{revoked: boolean} | false> => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/headquarters-access-grants/${grantUUID}`;
    const response = await baseAPI.getAxiosInstance().delete(url, {
      headers: setApiJSONHeaders(userInfo),
      data: {reason},
    });
    return response.data;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface ISearchHqManagerCandidates extends defaultProps {
  userInfo: IUserInfo | null;
  contractorUUID: string;
  keyword?: string;
  page?: number;
  count?: number;
}

/**
 * Phase 2.10 — grant 부여 모달용 시공사 관리자 후보 검색.
 * 응답: { contractorPermissions: [...], totalCount }
 * 각 permission 안에 user (uuid, profile, phone) + role 포함.
 */
export const searchHqManagerCandidates = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  contractorUUID,
  keyword = '',
  page = 1,
  count = 50,
}: ISearchHqManagerCandidates): Promise<
  {contractorPermissions: any[]; totalCount: number} | false
> => {
  try {
    setIsLoading(true);
    const url = `/v1/admins/headquarters-access-grants/manager-candidates`;
    const response = await baseAPI.getAxiosInstance().get(url, {
      headers: setApiJSONHeaders(userInfo),
      params: {contractorUUID, keyword, page, count},
    });
    return response.data;
  } catch (error: any) {
    setApiErrorModal(setErrorModal, error);
    return false;
  } finally {
    setIsLoading(false);
  }
};

interface IChangeTongPlaceHiddenState extends defaultProps {
  userInfo: IUserInfo | null;
  tongPlaceUUID: string;
  hiddenState: 'HIDDEN' | 'VISIBLE';
}

/**
관리자용 현장 숨김 상태 변경 API.
숨김(HIDDEN) 처리하면 앱의 "현장 찾아 출근신청" 과 PC관리자 현장 목록 등
일반 조회에서 제외된다. VISIBLE 로 되돌릴 수 있다.
 @param parameter
 @returns {boolean}
 @example
 ```typescript
const result = await changeTongPlaceHiddenState({
  userInfo,
  tongPlaceUUID,
  hiddenState: 'HIDDEN',
});
console.log(result); // true || false
```
 */
export const changeTongPlaceHiddenState = async ({
  userInfo,
  setIsLoading,
  setErrorModal,
  tongPlaceUUID,
  hiddenState,
}: IChangeTongPlaceHiddenState): Promise<boolean> => {
  try {
    setIsLoading(true);
    const url = `/v1/tong-places/back-office/admins/tong-places/hidden-states`;
    const response = await baseAPI.getAxiosInstance().patch(
      url,
      {
        tongPlaceUUID,
        hiddenState,
      },
      {
        headers: {
          'Access-Control-Allow-Origin': '*',
          Accept: 'application/json',
          Authorization: userInfo?.accessToken,
        },
      },
    );

    return response.status === 200;
  } catch (error: any) {
    if (error.response?.errorCode === 403) {
      setErrorModal({
        onOff: true,
        title: 'updatePermissionErrorTitle',
      });
    } else {
      setErrorModal({
        type: error.response?.type ? error.response?.type : null,
        onOff: true,
        status: error.response?.status,
        title: error.response?.title,
        detail: error.response?.detail,
        startTime: error.response?.startTime,
        endTime: error.response?.endTime,
        childStatusHistoryList: error.response?.childStatusHistoryList,
      });
    }
    return false;
  } finally {
    setIsLoading(false);
  }
};
